/**
 * Shared card search matching helper.
 * Used by Collection, Set browsing, and Deck editor search filters.
 */
import { getFullOracleText, getFullTypeLine } from '@/lib/cardUtils';
import type { ScryfallCard } from '@/types/card';

/**
 * Returns true when `queryLower` is empty or matches any of the provided fields
 * via case-insensitive substring matching.
 *
 * Callers pass pre-lowercased fields so performance-sensitive paths
 * (e.g. Collection with precomputed `name_lower`) avoid per-card toLowerCase().
 */
export function matchesSearchQuery(
  fields: { nameLower: string; typeLineLower: string; oracleTextLower?: string },
  queryLower: string,
): boolean {
  if (!queryLower) return true;
  if (fields.nameLower.includes(queryLower)) return true;
  if (fields.typeLineLower.includes(queryLower)) return true;
  if (fields.oracleTextLower && fields.oracleTextLower.includes(queryLower)) return true;
  return false;
}

/**
 * Face-aware convenience wrapper for callers holding a full `ScryfallCard`.
 *
 * Multi-face layouts (split, adventure, transform, modal DFC, reversible) have
 * `oracle_text`/`type_line` only on `card_faces`, so the aggregating helpers are
 * used to keep back/adventure faces searchable.
 */
export function matchesCardSearchQuery(card: ScryfallCard, queryLower: string): boolean {
  if (!queryLower) return true;
  return matchesSearchQuery(
    {
      nameLower: card.name?.toLowerCase() ?? '',
      typeLineLower: (getFullTypeLine(card) ?? '').toLowerCase(),
      oracleTextLower: (getFullOracleText(card) ?? '').toLowerCase(),
    },
    queryLower,
  );
}
